/*
 *   Copyright (c) 2026, CYBRA Corp. All rights reserved. 
 */
import { Injectable } from "@angular/core";
import { ServiceService } from "./service.service";
import { PropertyPatch } from "app/models/property-patch";

/**
 * Constants related to the "AbleNet Exp Return Device Validation" Service
*/
/** UUID for the "AbleNet Exp Return Device Validation" Service Type */
export const AbleNetExpReturnDevicesValidation_Type_Uuid = '0a6cd7ce-e9b1-475d-ac1a-632bd116f3d2';

/** Get Current State */
const FunctionKey_GetCurrentState = "get_state";
/** Function Process Return Devices */
const FunctionKey_ProcessReturnDevices = "process_return_devices";
const FunctionParameterKey_AssetEpcs = "asset_epcs";
/** Manually entered Inventory IDs, one entry per overridden EPC, encoded as "<epc>::<inventoryId>" */
const FunctionParameterKey_InventoryIdOverrides = "asset_epcs_overrides";
/** Function Clear All / Cancel */
const FunctionKey_ClearAll = "clear_all";
/** Function Clear Reads */
const FunctionKey_ClearReads = "clear_reads";
/** Handle RFID Redads */
const FunctionKey_HandleRfidReads = "handle_rfid_reads";
/** Handle RFID Reads from Handheld */
const FunctionKey_HandleRfidReadsHH = "handle_rfid_reads_hh";
/** Start Validation Attempt */
const FunctionKey_StartValidation = "start";
/** Stop validation Attempt */
const FunctionKey_StopValidation = "stop";



/**
 * Service which provides functions used by the "AbleNets RC Validation" process.
 * 
 * This calls Functions on "Ablenet's RC Validation" Service instances in Edgefinity and
 * returns the results.
 */
@Injectable()
export class AblenetExpReturnDevicesValidationService {

  constructor(
      private serviceService: ServiceService
  ) { }

    /**
     * Calls the "Get Current State" Function on the "AbleNet Exp Return Device Validation" Service with the given ID
     * and returns the result.
     */
    public getCurrentState(portalId: number | string): Promise<ValidationInfoDto> {
      return this.serviceService.executeFunctionByKey(portalId, FunctionKey_GetCurrentState, null);
    }

    /**
     * Calls the "Get Current State" Function on the "AbleNet Exp Return Device Validation" Service with the given ID
     * and returns the result.
     */
    public handleRfidReads(portalId: number | string): Promise<ValidationInfoDto> {
      return this.serviceService.executeFunctionByKey(portalId, FunctionKey_HandleRfidReads, null);
    }

      /**
     * Calls the "Handle RFID Reads: HH" Function on the "AbleNet Shipment Validation" Service with the given ID
     * and returns the result.
     */
    public handleRfidReadsFromHH(portalId: number | string): Promise<ValidationInfoDto> {
      return this.serviceService.executeFunctionByKey(portalId, FunctionKey_HandleRfidReadsHH, null);
    }

      /**
       * Calls the "Clear Reads" Function on the "AbleNet Exp Return Device Validation" Service with the given ID and 
       * returns the result.
       */
      public clearReads(portalId: number | string): Promise<ValidationInfoDto> {
        return this.serviceService.executeFunctionByKey(portalId, FunctionKey_ClearReads, null);
      }

      /**
       * Calls the "Clear All/Cancel" Function on the "AbleNet Exp Return Device Validation" Service with the given ID and 
       * returns the result.
       */
      public clearAll(portalId: number | string): Promise<ValidationInfoDto> {
        return this.serviceService.executeFunctionByKey(portalId, FunctionKey_ClearAll, null);
      }

      /**
       * Calls the "Process Order" Function on the "AbleNet Exp Return Device Validation" Service with the given ID and
       * returns the result. `selectedAssets` may include a manually entered `inventoryID` for an Asset whose
       * Inventory ID couldn't previously be auto-generated; when present it overrides the auto-generation.
       */
      public async processReturnDevices(portalId: number | string, selectedAssets: { epc: string, inventoryID?: string }[]): Promise<ValidationInfoDto> {
        const functionData: PropertyPatch[] = selectedAssets.map(asset => ({
                                                                                    property: FunctionParameterKey_AssetEpcs,
                                                                                    value: asset.epc
                                                                                  }));
        selectedAssets
          .filter(asset => !!asset.inventoryID)
          .forEach(asset => functionData.push({
            property: FunctionParameterKey_InventoryIdOverrides,
            value: `${asset.epc}::${asset.inventoryID}`
          }));

        return await this.serviceService.executeFunctionByKey(portalId, FunctionKey_ProcessReturnDevices, functionData);
      }

      /**
       * Calls the "Process Order" Function on the "AbleNet Exp Return Device Validation" Service with the given ID and 
       * returns the result.
       */
      public async start(portalId: number | string): Promise<ValidationInfoDto> {
        return await this.serviceService.executeFunctionByKey(portalId, FunctionKey_StartValidation, null);
      }

      /**
       * Calls the "Process Order" Function on the "AbleNet Exp Return Device Validation" Service with the given ID and 
       * returns the result.
       */
      public async stop(portalId: number | string): Promise<ValidationInfoDto> {
        return await this.serviceService.executeFunctionByKey(portalId, FunctionKey_StopValidation, null);
      }

}

/**
 * DTO to store the Asset details
 */
export interface AbleNetValidation_AssetDetailDto {
  /** EPC & asset ID value of the Tag read */
  epc: string;
  assetId: string;
  fnNumber?: string;
  orderNbr?: string;
  inventoryID?: string;
  shipmentNbr?: string;
  status?: string;
  inventoryIdInputRequired?: boolean;
}

/**
 * DTO representing the "State" the "Exp Return Device Validation Portal" Services
 * are currently in.  This also contains the information on any 
 * Validation Attempt being performed by the Service. 
 */
export interface ValidationInfoDto {
  readerStatus: AbleNetReaderStateEnum | null;
  assets: AbleNetValidation_AssetDetailDto[];
}

/**
 * Reader Status
 */
export enum AbleNetReaderStateEnum {
    Disconnected = 'Disconnected',
    Stopped = 'Stopped',
    Started = 'Started',
    Reconnecting = 'Reconnecting...',
    Restarting = 'Restarting...'
}

